/**
 * 「人手填的」电话号码 —— 团购接龙、微信群里收来的那种。
 *
 * ★ 为什么不用 `normalizePhone`(address/normalize.ts)。
 *
 *   那个是给**正经导入表**用的:一个表里的格式基本一致,
 *   它只处理科学计数法和前导零。而接龙表里同一列会同时出现:
 *
 *     02108416738   0 开头的国内写法
 *     64021552601   国家码 + **还带着** 0 的国内写法
 *     06421520855   0 + 国家码 + 号码(两种写法叠在一起)
 *     00223981586   00 国际前缀
 *     +64 21 ...    带加号和空格
 *     00000000000   人随手打的假号
 *
 *   拿 `normalizePhone` 去跑,后面三种会分别变成 `+64021552601`、
 *   `+646421520855`、`+640223981586` —— 全是错的,而错的号码在库里
 *   长得和对的一样,只有司机打不通那一刻才会发现。
 *   (这几个值都是从用户给的那张真表里抄出来的。)
 *
 * ★ 所以这里做的是**反复剥**:国际前缀、国家码、国内前导零,
 *   谁在最外面就先剥谁,直到剥不动。
 */

/** 剥到最后的国内有效号码(NSN)长度上下界 —— 超出这个范围的当填错 */
const NSN_MIN = 7;
const NSN_MAX = 11;

export interface LoosePhone {
  /** E.164,例如 `+6421552601` */
  e164: string;
  /** 国内有效号码(去掉国家码和前导零),例如 `21552601` */
  nsn: string;
}

/**
 * 规整成 E.164。剥不出一个像样的号码就返回 null。
 *
 * @param countryCode 纯数字的国家码,例如 `'64'`。空字符串表示不知道 ——
 *                    那就只做清理,不加国家码
 */
export function normalizeLoosePhone(
  raw: string | number | null | undefined,
  countryCode: string,
): LoosePhone | null {
  let s = String(raw ?? '').trim();
  if (!s) return null;

  // Excel 常把长数字存成科学计数法
  if (/^\d+(\.\d+)?e\+?\d+$/i.test(s)) s = Number(s).toFixed(0);

  let d = s.replace(/\D/g, '');
  if (!d) return null;

  const cc = countryCode.replace(/\D/g, '');

  /*
   * ★ 反复剥,而不是「判断一次属于哪种写法」。
   *
   *   `06421520855` 就是两层叠在一起(前导零 + 国家码),
   *   一次性分类的写法处理不了它 —— 而这个号在真表里就有。
   *
   * ★ 剥国家码要带**长度下限**:NSN 以 6 开头是合法的(新西兰的
   *   区号里有 6),`64123456` 是一个正经的固话号。
   *   不看长度就剥的话它会变成 `123456`,而那正好会被当成假号扔掉。
   */
  for (let i = 0; i < 4; i++) {
    if (cc && d.startsWith(cc) && d.length - cc.length >= NSN_MIN) {
      d = d.slice(cc.length);
      continue;
    }
    if (d.startsWith('0')) {
      // 前导零一次剥干净:`00000000000` 这种剥到空,正好落进「假号」
      d = d.replace(/^0+/, '');
      continue;
    }
    break;
  }

  if (!d) return null;
  if (d.length < NSN_MIN || d.length > NSN_MAX) return null;

  return { e164: cc ? `+${cc}${d}` : d, nsn: d };
}

/**
 * 明显是人随手打的假号。
 *
 * ★ 用户的原话:「如果电话号码明显的错误,比如 123456 000000 111111
 *   这类的就不下单」。
 *
 * ★ 只挡**明显**的。挡得太宽的代价是一个真客户被无声地跳过 ——
 *   而他在失败列表里只会显示一行「号码不对」,没人会去替他核。
 *   所以这里只认三种:同一个数字重复、连续递增/递减、太短。
 */
export function isObviouslyFakePhone(nsn: string): boolean {
  if (!/^\d+$/.test(nsn)) return true;
  if (nsn.length < NSN_MIN) return true;
  // 同一个数字重复:000000000 / 111111111
  if (/^(\d)\1+$/.test(nsn)) return true;
  // 连续递增或递减:123456789 / 987654321(含子串,6 位以上就算)
  const ASC = '0123456789012345678901234567890';
  const DESC = '9876543210987654321098765432109';
  for (let len = nsn.length; len >= 6; len--) {
    const part = nsn.slice(0, len);
    if (ASC.includes(part) || DESC.includes(part)) return true;
  }
  return false;
}

/**
 * 模糊比对用的尾号。
 *
 * ★ 为什么比尾号而不是比整串:我们库里那一边也是人手录的,
 *   同样有 `+64` / `64` / `0` 各种写法。两边都规整过之后本该一致,
 *   而「本该」在这类数据上不成立 —— 比最后 8 位能把两边的前缀差异
 *   全绕开,而 8 位在一个国家内已经足够区分人。
 */
export function phoneTail(nsn: string, n = 8): string {
  return nsn.length <= n ? nsn : nsn.slice(-n);
}
